import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export default function ForgotPasswordPage() {
	const { t } = useTranslation();
	const [email, setEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [sent, setSent] = useState(false);

	useDocumentMeta({
		title: `${t('auth.forgot_title')} | Equi-Market`,
		description: t('auth.forgot_subtitle'),
	});

	const submit = async (e) => {
		e.preventDefault();
		if (!email.trim()) return;
		setSubmitting(true);
		try {
			await authAPI.forgotPassword(email.trim().toLowerCase());
		} catch {
			// intentionally swallow — endpoint never reveals errors anyway
		} finally {
			setSent(true);
			setSubmitting(false);
		}
	};

	return (
		<div
			className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2] flex items-start md:items-center justify-center"
			data-testid="forgot-password-page"
		>
			<div className="w-full max-w-md mx-auto px-4 sm:px-6 py-10">
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 sm:p-8">
					<Link
						to="/auth"
						className="inline-flex items-center gap-1 text-xs text-[#8B4513] hover:underline mb-5"
						data-testid="forgot-back-link"
					>
						<ArrowLeft className="w-3 h-3" /> {t('auth.back_to_login')}
					</Link>

					{sent ? (
						<div className="text-center" data-testid="forgot-sent-state">
							<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E6F0EC] mb-4">
								<Check className="w-6 h-6 text-[#1A3C34]" />
							</div>
							<h1 className="text-2xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">
								{t('auth.forgot_sent_title')}
							</h1>
							<p className="text-sm text-[#666055] leading-relaxed">
								{t('auth.forgot_sent_body')}
							</p>
							<Button
								type="button"
								onClick={() => { setSent(false); setEmail(''); }}
								variant="outline"
								className="mt-6 border-[#E6E2D6] text-[#1A3C34] rounded-sm"
								data-testid="forgot-try-another-btn"
							>
								{t('auth.forgot_try_another')}
							</Button>
						</div>
					) : (
						<>
							<div className="flex items-center gap-2 mb-1">
								<Mail className="w-5 h-5 text-[#8B4513]" />
								<h1 className="text-2xl font-medium text-[#1A3C34] font-['Playfair_Display']">
									{t('auth.forgot_title')}
								</h1>
							</div>
							<p className="text-sm text-[#666055] mb-6">{t('auth.forgot_subtitle')}</p>

							<form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
								<div>
									<Label htmlFor="email" className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('auth.email')}
									</Label>
									<Input
										id="email"
										type="email"
										autoComplete="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="you@example.com"
										required
										className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-12 mt-1"
										data-testid="forgot-email-input"
									/>
								</div>
								<Button
									type="submit"
									disabled={submitting || !email.trim()}
									className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 text-base font-medium btn-equi"
									data-testid="forgot-submit-btn"
								>
									{submitting ? t('common.loading') : t('auth.forgot_submit')}
								</Button>
							</form>
						</>
					)}
				</div>
			</div>
		</div>
	);
}